// MARK://dhk8/admin-shell/pages/modules/NewPurchaseOrderPage
// Alta de orden de compra con LÍNEAS de producto — master-detail, NO un CRUD
// genérico. La cabecera (proveedor + notas) y un editor de líneas (producto,
// cantidad, UOM, costo unitario, total) viven en la misma pantalla.
//
// DS: Proveedor y Producto se eligen con EntityCombobox (select con búsqueda);
// la UOM de cada línea usa UnitPicker. Al crear → navega al detalle; la lista de
// OC se recarga sola al volver (useAsync corre on-mount).
//
// Si el proveedor tiene networkId, se hereda a la OC: al enviarla, el puente
// cross-entity la enruta a la bandeja del tenant proveedor (Flujo 3).
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Trash2, ArrowLeft, Save } from 'lucide-react'
import * as po from '../../lib/clients/purchase-orders.js'
import * as sup from '../../lib/clients/suppliers.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import UnitPicker from '../../components/UnitPicker.js'
import EntityCombobox from '../../components/EntityCombobox.js'
import MoneyInput from '../../components/MoneyInput.js'
import { useToast } from '../../components/Toast.js'

interface DraftLine {
  key: string
  product: po.PurchasableProduct | null // objeto seleccionado (para el combobox)
  qty: number
  uom: string
  unitPesos: number // captura en pesos; se convierte a centavos al guardar
}
const newLine = (): DraftLine => ({ key: `l-${Math.random().toString(36).slice(2, 8)}`, product: null, qty: 1, uom: 'KG', unitPesos: 0 })

// Subtítulo de sección discreto — marca NintaiGO: --font-mono, --text-tertiary,
// uppercase pequeño. Conserva curvas/tokens; agrupa el form por densidad.
function FormSection({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      margin: '4px 0 2px', fontFamily: 'var(--font-mono)', fontSize: 11,
      letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)',
    }}>{children}</div>
  )
}

export default function NewPurchaseOrderPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const suppliers = useAsync((s) => sup.listSuppliers(s))
  const products = useAsync((s) => po.listPurchasableProducts(s))

  const [supplier, setSupplier] = useState<sup.Supplier | null>(null)
  const [notes, setNotes] = useState('')
  const [lines, setLines] = useState<DraftLine[]>([newLine()])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // fetchOptions client-side: filtra las listas ya cargadas en memoria.
  const fetchSuppliers = async (q: string): Promise<sup.Supplier[]> => {
    const all = suppliers.data ?? []
    const s = q.trim().toLowerCase()
    const filtered = s ? all.filter((x) => x.legalName.toLowerCase().includes(s) || (x.rfc ?? '').toLowerCase().includes(s)) : all
    return filtered.slice(0, 50)
  }
  const fetchProducts = async (q: string): Promise<po.PurchasableProduct[]> => {
    const all = products.data ?? []
    const s = q.trim().toLowerCase()
    const filtered = s ? all.filter((p) => p.name.toLowerCase().includes(s) || (p.sku ?? '').toLowerCase().includes(s)) : all
    return filtered.slice(0, 50)
  }

  const lineTotalCents = (l: DraftLine): number => Math.round(l.qty * Math.round(l.unitPesos * 100))
  const totalCents = lines.reduce((s, l) => s + lineTotalCents(l), 0)

  function patchLine(key: string, patch: Partial<DraftLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  }
  function removeLine(key: string) {
    setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.key !== key) : ls))
  }

  function validate(): string | null {
    if (!supplier) return 'Selecciona un proveedor.'
    if (lines.length === 0) return 'Agrega al menos una línea.'
    for (const l of lines) {
      if (!l.product) return 'Cada línea necesita un producto.'
      if (l.qty <= 0) return 'La cantidad de cada línea debe ser mayor a 0.'
      if (l.unitPesos < 0) return 'El costo unitario no puede ser negativo.'
    }
    return null
  }

  async function save(thenSubmit: boolean) {
    const v = validate()
    if (v) { setError(v); return }
    setError(null); setSaving(true)
    try {
      const created = await po.createPurchaseOrder({
        supplierId: supplier!.id,
        ...(supplier!.networkId ? { networkId: supplier!.networkId } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        lines: lines.map((l) => ({
          productId: l.product!.id,
          qty: l.qty,
          uom: l.uom,
          unitCents: Math.round(l.unitPesos * 100),
        })),
      })
      if (thenSubmit) {
        try {
          await po.submitPurchaseOrder(created.id)
          toast('Orden creada y enviada', 'success')
        }
        catch (e) {
          // La OC quedó creada como borrador; el envío falló — avisa pero navega.
          toast(`OC creada como borrador, pero el envío falló: ${e instanceof Error ? e.message : 'error'}`, 'warn', { duration: 7000 })
        }
      } else {
        toast('Borrador de orden creado', 'success')
      }
      navigate(`/purchase-orders/${created.id}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo crear la orden de compra.')
    } finally { setSaving(false) }
  }

  const selectedSupplier = useMemo(() => supplier, [supplier])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginBottom: 6 }} onClick={() => navigate('/purchase-orders')}><ArrowLeft size={15} /> Órdenes de compra</button>
          <h1 className="adm-page__title">Nueva orden de compra</h1>
          <p className="adm-page__sub">Captura el proveedor y las líneas de producto</p>
        </div>
      </div>

      {asyncState(suppliers.loading, suppliers.error, suppliers.reload)}

      {error && <div className="adm-banner adm-banner--danger" role="alert" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Cabecera — Datos básicos */}
      <div className="adm-card adm-card--pad">
        <h2 className="adm-card__title">Cabecera</h2>

        <FormSection>Proveedor</FormSection>
        <label className="adm-field">
          <span className="adm-field__label">Proveedor</span>
          <EntityCombobox
            value={supplier}
            onChange={setSupplier}
            fetchOptions={fetchSuppliers}
            renderOption={(s) => <span>{s.legalName}{s.rfc ? <span className="adm-muted"> · {s.rfc}</span> : null}</span>}
            getOptionValue={(s) => s.id}
            getOptionLabel={(s) => s.legalName}
            placeholder="Buscar proveedor…"
            allowClear
          />
        </label>
        {selectedSupplier?.networkId && (
          <div className="adm-banner" role="status" style={{ marginTop: 4 }}>
            Este proveedor está enlazado a la red. Al <strong>enviar</strong> la orden, llegará a su bandeja para autorización.
          </div>
        )}

        <FormSection>Notas</FormSection>
        <label className="adm-field">
          <span className="adm-field__label">Notas (opcional)</span>
          <textarea className="adm-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reorden de café en grano — stock bajo" rows={2} />
        </label>
      </div>

      {/* Líneas */}
      <div className="adm-card adm-card--pad">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h2 className="adm-card__title" style={{ margin: 0 }}>Líneas de producto</h2>
          <button className="adm-btn adm-btn--sm" onClick={() => setLines((ls) => [...ls, newLine()])}><Plus size={15} /> Agregar línea</button>
        </div>
        <div className="adm-list">
          {lines.map((l) => (
            <div key={l.key} className="adm-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="adm-row__main" style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.9fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                <label className="adm-field" style={{ margin: 0 }}>
                  <span className="adm-field__label">Producto</span>
                  <EntityCombobox
                    value={l.product}
                    onChange={(p) => patchLine(l.key, { product: p })}
                    fetchOptions={fetchProducts}
                    renderOption={(p) => <span>{p.name}{p.sku ? <span className="adm-muted"> · {p.sku}</span> : null}</span>}
                    getOptionValue={(p) => p.id}
                    getOptionLabel={(p) => p.name}
                    placeholder="Buscar producto…"
                    allowClear
                  />
                </label>
                <label className="adm-field" style={{ margin: 0 }}>
                  <span className="adm-field__label">Cantidad</span>
                  <input className="adm-input" type="number" min={0} step="0.01" value={l.qty} onChange={(e) => patchLine(l.key, { qty: Number(e.target.value) })} />
                </label>
                <div style={{ margin: 0 }}>
                  {/* A: UOM por lista (clave SAT) — ya no es texto libre. */}
                  <UnitPicker label="UOM" value={l.uom} onChange={(uom) => patchLine(l.key, { uom })} />
                </div>
                <label className="adm-field" style={{ margin: 0 }}>
                  <span className="adm-field__label">Costo unitario</span>
                  <MoneyInput valueCents={Math.round(l.unitPesos * 100)} onChangeCents={(c) => patchLine(l.key, { unitPesos: c / 100 })} />
                </label>
                <label className="adm-field" style={{ margin: 0 }}>
                  <span className="adm-field__label">Total línea</span>
                  <input className="adm-input" value={mxn(lineTotalCents(l))} disabled aria-readonly="true" style={{ fontFamily: 'var(--font-mono)' }} />
                </label>
                <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeLine(l.key)} disabled={lines.length <= 1} aria-label="Quitar línea"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, fontSize: 'var(--pos-text-lg)', fontWeight: 700 }}>
          Total: {mxn(totalCents)}
        </div>
      </div>

      <div className="adm-page__actions" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
        <button className="adm-btn" onClick={() => save(false)} disabled={saving}><Save size={16} /> {saving ? 'Guardando…' : 'Guardar borrador'}</button>
        <button className="adm-btn adm-btn--primary" onClick={() => save(true)} disabled={saving}>{saving ? 'Procesando…' : 'Guardar y enviar'}</button>
      </div>
    </div>
  )
}
